"use client";

import { createContext, useCallback, useContext, useSyncExternalStore } from "react";

/**
 * Adapter de navegação por query params injetado pelo app consumidor.
 *
 * Os blocos são framework-agnostic e NÃO podem importar `next/*` (senão o
 * bundle federado carrega no builder Vite com um specifier `next/navigation`
 * impossível de resolver, derrubando o editor). Cada app injeta sua própria
 * implementação:
 *   - renderer (Next): usa next/navigation (useRouter/usePathname/useSearchParams)
 *   - UI/builder (Vite): usa o default window-based abaixo (modo edição não
 *     precisa de navegação real)
 */
export interface QueryParamsAdapter {
  /** Snapshot reativo dos query params atuais (para leitura). */
  searchParams: URLSearchParams;
  /** Empurra uma nova query string (mantém o pathname, sem scroll). */
  pushQuery: (queryString: string) => void;
  /**
   * Troca a query SEM navegação nem re-fetch do servidor, mantendo o router
   * ciente da mudança. Para o estado que vive só na URL (a escolha do
   * Comparador), em vez de um `history.replaceState` cru que o Next não enxerga.
   */
  replaceQuery: (queryString: string) => void;
  /** Indica navegação pendente (transition). */
  isPending: boolean;
}

const QueryParamsAdapterContext = createContext<QueryParamsAdapter | null>(null);

export const QueryParamsAdapterProvider = QueryParamsAdapterContext.Provider;

// --- Default window-based (fallback framework-agnostic, sem next) ---

const QUERY_CHANGE_EVENT = "atd:querychange";

function subscribe(callback: () => void) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener("popstate", callback);
  window.addEventListener(QUERY_CHANGE_EVENT, callback);
  return () => {
    window.removeEventListener("popstate", callback);
    window.removeEventListener(QUERY_CHANGE_EVENT, callback);
  };
}

function getSnapshot() {
  return typeof window === "undefined" ? "" : window.location.search;
}

function getServerSnapshot() {
  return "";
}

function useDefaultQueryParamsAdapter(): QueryParamsAdapter {
  const search = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const pushQuery = useCallback((queryString: string) => {
    if (typeof window === "undefined") return;
    const url = `${window.location.pathname}${queryString ? `?${queryString}` : ""}${window.location.hash}`;
    window.history.pushState(null, "", url);
    window.dispatchEvent(new Event(QUERY_CHANGE_EVENT));
  }, []);

  const replaceQuery = useCallback((queryString: string) => {
    if (typeof window === "undefined") return;
    const url = `${window.location.pathname}${queryString ? `?${queryString}` : ""}${window.location.hash}`;
    window.history.replaceState(null, "", url);
    window.dispatchEvent(new Event(QUERY_CHANGE_EVENT));
  }, []);

  return {
    searchParams: new URLSearchParams(search),
    pushQuery,
    replaceQuery,
    isPending: false,
  };
}

/**
 * Retorna o adapter injetado pelo provider; se não houver provider (ex: builder
 * sem wrapper), cai no default window-based. As regras de hooks exigem chamar o
 * default incondicionalmente — quando há provider, o resultado é só descartado.
 */
export function useQueryParamsAdapter(): QueryParamsAdapter {
  const injected = useContext(QueryParamsAdapterContext);
  const fallback = useDefaultQueryParamsAdapter();
  return injected ?? fallback;
}
